import {canBeOptimisticallyParsed, md2html} from '@github-ui/md2html'
import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'

/** Identifies the connection a comment write targets for TanStack-backed readers. */
export type OptimisticConnection = {ownerId: string; connectionPath: string[]}

type CommentMinimizationFields = {
  isHidden: boolean
  minimizedReason: string | null
  pendingMinimizeReason: string | null
}

/**
 * Bridges a Relay comment mutation into the optimistic-connection overlay.
 *
 * The overlay is a no-op unless a bridge is mounted for the same owner and path, so callers that
 * render from Relay are unaffected.
 */
export function beginAddCommentOverlay({
  connection: {ownerId, connectionPath},
  node,
}: {
  connection: OptimisticConnection
  node: (Record<string, unknown> & {id?: string | null}) | null | undefined
}) {
  const mutationId = createConnectionMutationId()
  const temporaryId = typeof node?.id === 'string' ? node.id : undefined
  const counts = [{path: [...connectionPath, 'totalCount'], delta: 1}]

  const operationFor = (payload: Record<string, unknown>): ConnectionOperation => {
    const payloadId = payload['id']
    return {
      type: 'addToConnection',
      connectionPath,
      // The confirmed node keeps its own server id; the temporary one rides along as the
      // correlation key, which is what stops the still-pending add rendering as a second row.
      node: {
        ...payload,
        id: typeof payloadId === 'string' ? payloadId : (temporaryId ?? mutationId),
        ...(temporaryId ? {[OPTIMISTIC_KEY]: temporaryId} : {}),
      },
      anchor: {at: 'end'},
      counts,
    }
  }

  // Relay declines an optimistic response for some bodies (a task list) and before its store is
  // populated. The bridge only settles mutations it registered, so a phase must still be
  // dispatched or the comment never appears -- `setFields` with none registers the settler only.
  dispatchConnectionMutation({
    phase: 'optimistic',
    mutationId,
    ownerId,
    connectionPath,
    operation: node && temporaryId ? operationFor({...node}) : {type: 'setFields', id: ownerId, fields: {}},
  })

  return {
    settle(confirmed: (Record<string, unknown> & {id?: string | null}) | null | undefined) {
      const settled = confirmed ?? node
      dispatchConnectionMutation({
        phase: 'completed',
        mutationId,
        ownerId,
        connectionPath,
        // With no optimistic row there is nothing to correlate, so the settle only has to trigger
        // the base refresh; the refetch is what surfaces the comment.
        operation: settled ? operationFor({...settled}) : {type: 'setFields', id: ownerId, fields: {}},
      })
    },
    fail(error?: Error) {
      dispatchConnectionMutation({phase: 'error', mutationId, error: error ?? new Error('Add comment failed')})
    },
  }
}

/** Removes a comment row from the overlay while its Relay delete is in flight. */
export function beginDeleteCommentOverlay({
  connection: {ownerId, connectionPath},
  nodeId,
}: {
  connection: OptimisticConnection
  nodeId: string
}) {
  const mutationId = createConnectionMutationId()
  const operation: ConnectionOperation = {
    type: 'removeFromConnection',
    connectionPath,
    nodeId,
    counts: [{path: [...connectionPath, 'totalCount'], delta: -1}],
  }

  dispatchConnectionMutation({phase: 'optimistic', mutationId, ownerId, connectionPath, operation})

  return {
    settle() {
      dispatchConnectionMutation({phase: 'completed', mutationId, ownerId, connectionPath, operation})
    },
    fail(error?: Error) {
      dispatchConnectionMutation({phase: 'error', mutationId, error: error ?? new Error('Delete comment failed')})
    },
  }
}

/** Updates a comment's minimization fields while the server mutation is in flight. */
export function beginCommentMinimizationOverlay({
  connection: {ownerId, connectionPath},
  nodeId,
  fields,
}: {
  connection: OptimisticConnection
  nodeId: string
  fields: CommentMinimizationFields
}) {
  const mutationId = createConnectionMutationId()
  const operationFor = (nextFields: CommentMinimizationFields): ConnectionOperation => ({
    type: 'setFields',
    id: nodeId,
    fields: nextFields,
  })

  dispatchConnectionMutation({
    phase: 'optimistic',
    mutationId,
    ownerId,
    connectionPath,
    operation: operationFor(fields),
  })

  return {
    settle(confirmed: CommentMinimizationFields) {
      dispatchConnectionMutation({
        phase: 'completed',
        mutationId,
        ownerId,
        connectionPath,
        operation: operationFor(confirmed),
      })
    },
    fail(error?: Error) {
      dispatchConnectionMutation({phase: 'error', mutationId, error: error ?? new Error('Minimize comment failed')})
    },
  }
}

/**
 * Shows an edited body on a comment row while its Relay update is in flight, and writes the server's
 * own values in when it settles.
 *
 * A body the client cannot render faithfully -- a task list, in practice -- is not guessed at:
 * Relay declines its own optimistic response for the same bodies, and where there is no Relay this
 * is what renders instead, so a guess would put exactly the markup that check avoids on screen.
 * The settle still runs for those, or the row would keep the old body until an unrelated refetch.
 */
export function beginUpdateCommentOverlay({
  connection: {ownerId, connectionPath},
  nodeId,
  body,
}: {
  connection: OptimisticConnection
  nodeId: string
  body: string
}) {
  const mutationId = createConnectionMutationId()
  const guess: ConnectionOperation = {type: 'setFields', id: nodeId, fields: {body, bodyHTML: md2html(body)}}
  const showsGuess = canBeOptimisticallyParsed(body)

  if (showsGuess) {
    dispatchConnectionMutation({phase: 'optimistic', mutationId, ownerId, connectionPath, operation: guess})
  }

  return {
    /** Without `confirmed` there is nothing trustworthy to write, so a shown guess is rolled back. */
    settle(confirmed?: Record<string, unknown>) {
      if (!confirmed) return showsGuess ? this.fail(new Error('Update comment returned no comment')) : undefined

      // A settle with no optimistic phase lands as an orphan completion, which is what carries the
      // server's values onto a row whose body was never guessed at.
      dispatchConnectionMutation({
        phase: 'completed',
        mutationId,
        ownerId,
        connectionPath,
        operation: {type: 'setFields', id: nodeId, fields: confirmed},
      })
    },
    fail(error?: Error) {
      if (!showsGuess) return
      dispatchConnectionMutation({phase: 'error', mutationId, error: error ?? new Error('Update comment failed')})
    },
  }
}
